import React, { useState } from 'react';
import { Platform, Pressable } from 'react-native-web';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { Location } from './SidebarLocation';

import { useMount } from '../../../hooks';
import StaticMap from './StaticMap';
import { PublicProvider } from '../../../api/Providers';
import type { ProviderScheduleDay } from '../../../store/ProviderWorkSchedule.types';
import analytics from '../../../modules/analytics';
import {
  BodyText,
  Button,
  HeaderText,
  Stack,
} from '../../ui';
import { ChattingCommentTextIcon } from '../../ui/Icons/ChattingCommentTextIcon';
import { TimeCalendarCheckedIcon } from '../../ui/Icons/TimeCalendarCheckedIcon';
import { CircleCheckedIcon } from '../../ui/Icons/CircleCheckedIcon';
import { StarIcon } from '../../ui/Icons/StarIcon';
import { AboutBusinessHours } from './BusinessHours/BusinessHours';
import { ClientProviderProfile } from '../../../api/ProfileClientView';
import { isGoogleBot } from '../../../modules/BrowserInfo';
import { TopProBadge } from '../TopPro/TopProBadge';
import { getProfileKeyHighlights } from '../../../modules/provider/profileKeyHighlights';

type SidebarInfoProps = {
  isEditMode?: boolean;
  profile: ClientProviderProfile;
  onMessagePro: () => void;
  schedule: ProviderScheduleDay[];
  isTopPro?: boolean;
  onTopProClick?: () => void;
  provider: PublicProvider | ClientProviderProfile;
};

export const SidebarInfo: React.FCWithChildren<SidebarInfoProps> = ({
  isEditMode = false,
  profile,
  onMessagePro,
  schedule,
  isTopPro,
  onTopProClick,
  provider,
}) => {
  const [renderStaticMap, setRenderStaticMap] = useState(false);
  const highlights = getProfileKeyHighlights(provider);
  const has1KPusBooking = highlights.oneThousandPlusBooked;
  const { styleSeatVerified } = highlights;
  const isLicensed = highlights.licensed;
  /**
   * Checks if we are being crawled by prerender.io which generates
   * unnecessary Google API fees by displaying maps to bots.
   */
  const checkPrerender = () => {
    if (Platform.OS !== 'web') {
      return setRenderStaticMap(true);
    }

    const shouldHide = isGoogleBot();

    if (shouldHide) {
      analytics.track('client_profile_map_hidden');
    }

    return setRenderStaticMap(!shouldHide);
  };

  useMount(() => {
    checkPrerender();
  });

  const aboutMe = profile?.blurb;
  const hasAddress = !!profile?.zipcode;
  return (
    <Stack
      testID="right_colum_slot_revamp"
      overflow="hidden"
      borderRadius={16}
    >
      {/* map */}
      {renderStaticMap ? (
        <StaticMap profile={profile} />
      ) : null}
      <Stack padding={24} space={36}>
        {/* address */}
        {hasAddress && (
          <Location profile={profile} />
        )}
        <Stack space={16}>
          {/* About me */}
          <Box>
            <HeaderText level={3} color={BrandColor.BackInBlack}>
              About Me
            </HeaderText>
          </Box>
          {/* height lights */}
          <Inline flexWrap="wrap">
            {isTopPro && (
              <Box paddingRight={12}>
                <Pressable onPress={onTopProClick} testID="TopProBadge">
                  <TopProBadge />
                </Pressable>
              </Box>
            )}
            {isLicensed && (
              <Box paddingBottom={12} paddingRight={12}>
                <Inline
                  testID="pro-1k+-booked"
                  justifyContent="center"
                  alignItems="center"
                  color={BrandColor.FloraLight}
                  space={4}
                  paddingY={4}
                  paddingX={8}
                  borderRadius={100}
                >
                  <StarIcon
                    width={12}
                    height={12}
                    color={BrandColor.FloraDark}
                  />
                  <BodyText color={BrandColor.Metal}>Licensed</BodyText>
                </Inline>
              </Box>
            )}
            {has1KPusBooking && (
              <Box paddingBottom={12} paddingRight={12}>
                <Inline
                  testID="pro-1k+-booked"
                  justifyContent="center"
                  alignItems="center"
                  color={BrandColor.FloraLight}
                  space={4}
                  paddingY={4}
                  paddingX={8}
                  borderRadius={100}
                >
                  <TimeCalendarCheckedIcon
                    width={12}
                    height={12}
                    color={BrandColor.FloraDark}
                  />
                  <BodyText color={BrandColor.Metal}>1K+ booked</BodyText>
                </Inline>
              </Box>
            )}
            {styleSeatVerified && (
              <Box paddingBottom={12}>
                <Inline
                  testID="pro-1k+-booked"
                  justifyContent="center"
                  alignItems="center"
                  color={BrandColor.FloraLight}
                  space={4}
                  paddingY={4}
                  paddingX={8}
                  borderRadius={100}
                >
                  <CircleCheckedIcon
                    width={12}
                    height={12}
                    color={BrandColor.FloraDark}
                  />
                  <BodyText color={BrandColor.Metal}>StyleSeat Verified</BodyText>
                </Inline>
              </Box>
            )}
          </Inline>
          {aboutMe ? (
            <Box paddingBottom={8}>
              <BodyText appearance="normal" color={BrandColor.Metal}>
                {profile?.blurb}
              </BodyText>
            </Box>
          ) : null}
          {/* message button */}
          {isEditMode ? null : (
            <Box
              color={BrandColor.SuperLight}
              width={143}
              height={56}
              justifyContent="center"
              alignItems="center"
              borderRadius={8}
            >
              <Inline
                space={12}
                justifyContent="center"
                alignItems="center"
              >
                <ChattingCommentTextIcon
                  width={24}
                  height={24}
                  color={BrandColor.Metal}
                />
                <Button
                  onClick={onMessagePro}
                  appearance="text"
                  style={{
                    lineHeight: '30px',
                  }}
                  testID="messageButton"
                >
                  Message
                </Button>
              </Inline>
            </Box>
          )}
        </Stack>
        {/* business hours */}
        <Box>
          <AboutBusinessHours schedule={schedule} centered />
        </Box>
        {/* Deposit policy */}
        {!isEditMode && profile?.deposit_policy_text ? (
          <Box>
            <Stack space={12}>
              <HeaderText level="3" color={BrandColor.BackInBlack}>Deposit Policy</HeaderText>
              <BodyText
                appearance="normal"
                testID="depositPolicy--text"
                color={BrandColor.Metal}
              >
                {profile.deposit_policy_text}
              </BodyText>
            </Stack>
          </Box>
        ) : null}
        {/* Cancelation policy */}
        {!isEditMode && profile?.cancellation_policy_text ? (
          <Box>
            <Stack space={12}>
              <HeaderText level="3" color={BrandColor.BackInBlack}>No-Show / Late Cancellation Policy</HeaderText>
              <BodyText
                appearance="normal"
                testID="cancellationPolicy--text"
                color={BrandColor.Metal}
              >
                {profile.cancellation_policy_text}
              </BodyText>
            </Stack>
          </Box>
        ) : null}
      </Stack>
    </Stack>
  );
};
